<ng-container
  [appPreloader]="[endpoints.FILE_POST, endpoints.PROPERTY_CORELOGIC_STATS_GET, endpoints.PROPERTIES_VALUATIONS_POST, endpoints.PROPERTIES_VALUATIONS_PUT]"
  [preloaderType]="preloaderTypes.OVERLAY"></ng-container>

<form [formGroup]="form" (ngSubmit)="submit()" appFormErrorScroll>
  @if (!valuation.id && !isSwitcherHidden) {
    <mat-radio-group formControlName="isCorelogic">
      <mat-radio-button [value]="true">CoreLogic</mat-radio-button>
      <mat-radio-button [value]="false">Manual</mat-radio-button>
    </mat-radio-group>
  }

  @if (form.value.isCorelogic && corelogicStats) {
    <div>
      <div class="d-flex mb-15">
        <i class="icon icon-corelogic-full is-medium ic-error"></i>
        <i class="icon-info fs-default ml-5"
           matTooltip="TaxTank has partnered with Corelogic to provide greater insight into potential future property performance."></i>
      </div>
      <ng-container class="mb-15">
        @if (corelogicStats.marketValue) {
          <div>
            <div class="align-h-space-between">
              <div class="h7">
                Estimated Market Value & AVM Range
                <i class="icon-info ml-5"
                   matTooltip="Choose your Market Value from CoreLogic’s AVM Range using the Estimated Market Value slider, or check the “Manual” box to enter your own value. For more details, click the 'CoreLogic disclaimers' button below to view CoreLogic's full AVM Estimate disclaimer and AVM Range definitions."></i>
              </div>
              @if (valuation.id) {
                <mat-checkbox (change)="isManualMV = !isManualMV">Select manual</mat-checkbox>
              }
            </div>
            <div class="h7">
              Confidence Score: {{ corelogicStats.confidence }}
              <i class="icon-info ml-5"
                 matTooltip="What is the CoreLogic Confidence Score? This score reflects how reliable the estimated market value is, based on the amount and quality of comparable property data available in the area. High confidence: Typically based on a greater number of comparable properties and/or low variability between the subject and comparable properties. Medium confidence: Based on a moderate number of comparable properties and/or moderate variability. Low confidence: Typically due to fewer comparable properties and/or high variability. This estimate should be used with caution."></i>
            </div>
            <mat-slider [displayWith]="currencyTransformer"
                        [min]="corelogicStats.lowMarketValue"
                        [max]="corelogicStats.highMarketValue"
                        step="1000"
                        discrete>
              <input matSliderThumb formControlName="marketValue"/>
            </mat-slider>
            <div class="slider-labels mb-15">
              <span class="label">
                <span class="cursor-pointer"
                      matTooltip="Low confidence typically results from fewer available comparable properties and/or a high degree of variability between the subject and comparable properties in the area and/or low availability of comparable property data in the area. This AVM is not reliable and should only be used with caution.">Low:</span>
                {{ corelogicStats.lowMarketValue | customCurrency: { decimals: 0 } }}
              </span>
              <span class="label active">
                <span class="cursor-pointer"
                      matTooltip=" Medium confidence is typically a result of a medium degree of available comparable properties and variability between the subject and comparable properties in the area and/or medium availability of comparable property data in the area.">Estimate:</span>
                {{ corelogicStats.marketValue | customCurrency: { decimals: 0 } }}
              </span>
              <span class="label">
                <span class="cursor-pointer"
                      matTooltip="High confidence is typically a result of a greater number of available comparable properties and/or a lesser degree of variability between the subject and comparable properties and/or high availability of comparable property data in the area.">High:</span>
                {{ corelogicStats.highMarketValue | customCurrency: { decimals: 0 } }}
              </span>
            </div>
          </div>
        }
        @if (isManualMV) {
          <div>
            <app-currency-input min="0"
                                [disabled]="!!corelogicStats.marketValue"
                                inputId="onBoardingPropertyCurrentAdd"
                                formControlName="marketValue"
                                label="Market Value"
                                labelPostfix=", AUD"
                                placeholder="Enter Market Value">
            </app-currency-input>
          </div>
        }
      </ng-container>
      <div class="mb-15">
        <div class="h7">
          Forecasted Property Growth
          <i class="icon-info ml-5"
             matTooltip="We use CoreLogic market trends data, which highlights the historical 10-year monthly average growth rate for your suburb. This data serves as a general benchmark when selecting a forecasted growth rate for your property. However, it’s important to note that CoreLogic’s data reflects past performance at the suburb level and is not a prediction of future growth or specific to your property.">
          </i>
        </div>
        <mat-slider [displayWith]="percentTransformer"
                    [min]="growthPercentMin"
                    [max]="growthPercentMax"
                    [discrete]="true"
                    [step]="percentageStep">
          <input matSliderThumb formControlName="growthPercent"/>
        </mat-slider>
        <div class="slider-labels">
          <span class="label">{{ growthPercentMin | number: '1.1-2' }}%</span>
          <span class="label active">{{ corelogicStats.growthPercent | number: '1.1-2' }}%</span>
          <span class="label">{{ growthPercentMax | number: '1.1-2' }}%</span>
        </div>
      </div>
      @if (corelogicStats.growthPercent && corelogicStats.marketValue) {
        <div class="h7">
          Use the sliders to set your Growth Percentage and choose a Market Value from CoreLogic’s AVM Range, or check
          the “Manual” box to enter your own value.
          CoreLogic updates are available monthly @if (isSwitcherHidden) {
          <span>from the Equity tab after a property has been added.</span>
        }
        </div>
      }
      @if (!corelogicStats.growthPercent || !corelogicStats.marketValue) {
        <div class="h7">
          CoreLogic data for Growth Percentage, Market Value, or both is currently unavailable for this property.
          Typically, updates are available monthly when available
          @if (isSwitcherHidden) {
            <span>and can be refreshed from the Equity tab as soon as they become available.</span>
          }
        </div>
      }
      @if (property.corelogicLastRequest) {
        (last updated  {{ property.corelogicLastRequest | date }})
      }
    </div>
  }

  @if (form.value.isCorelogic && !isCorelogicAvailable) {
    <div>
      <app-currency-input min="0"
                          inputId="onBoardingPropertyCurrentAdd"
                          formControlName="marketValue"
                          label="Market Value"
                          labelPostfix=", AUD"
                          placeholder="Enter Market Value">
      </app-currency-input>
      <mat-form-field>
        <mat-label>Growth Percent</mat-label>
        <input matInput type="number" formControlName="growthPercent">
      </mat-form-field>
      <div class="h7">
        CoreLogic data is refreshed monthly.
        The last update was {{ property.corelogicLastRequest | date }}, so the next update will be
        available {{ property.corelogicNextDate | date }}.
      </div>
    </div>
  }

  @if (!form.value.isCorelogic) {
    <div class="row">
      <!-- Market Value Input -->
      <div class="fw-600 c-secondary">Property Market Value</div>
      <div class="h7">Enter the current market value in AUD.</div>
      <div class="col-12">
        <app-currency-input min="0"
                            inputId="onBoardingPropertyCurrentAdd"
                            formControlName="marketValue"
                            label="Market Value"
                            labelPostfix=", AUD"
                            placeholder="Enter Market Value">
        </app-currency-input>
      </div>
      <div class="col-12 mb-15">
        <div class="fw-600 c-secondary">Forecasted Property Growth
          <i class="icon-info ml-5"
             matTooltip="Growth percentages are used to power the equity forecast graphs within Property Tank. These forecasts can be updated at any time."></i>
        </div>
        <div class="h7">
          Select the annual growth percentage from the slider below
        </div>
        <mat-slider [displayWith]="percentTransformer"
                    [min]="growthPercentMin"
                    [max]="growthPercentMax"
                    [discrete]="true"
                    [step]="percentageStep">
          <input matSliderThumb formControlName="growthPercent"/>
        </mat-slider>
        <div class="slider-labels">
          <span class="label">{{ growthPercentMin | number: '1.1-2' }}%</span>
          <span class="label active">{{ growthPercentMid | number: '1.1-2' }}%</span>
          <span class="label">{{ growthPercentMax | number: '1.1-2' }}%</span>
        </div>
      </div>
      <!-- Document Drag'n'Drop -->
      <div formGroupName="document" class="col-12">
        <app-file-uploader formControlName="file"
                           [fileTypes]="['image', 'document']"
                           mode="dragdrop"
                           caption="Property document"
                           label="Upload document">
        </app-file-uploader>
      </div>
    </div>
  }
</form>
